import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from "react";
import { useGetQueriesQuery } from "../../service/apiSlices/sematicModel";
import queryActionReducer from "./QueryActionsReducer";
import { useQueryTablesDataContext } from "./QueryTablesDataContext";
import { useLocation } from "react-router-dom";
import { isEmpty } from "lodash";

const QueryGeneratorContext = createContext({});
export const useQueryGeneratorContext: any = () =>
  useContext(QueryGeneratorContext);

const QueryGeneratorProvider = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();

  const semanticData = sessionStorage.getItem("SemanticData");
  const parsedData = semanticData ? JSON.parse(semanticData) : null;
  const secretKey = parsedData?.secret_key || location?.state?.Id || "";

  const { overallQueryItemMapByTableName } = useQueryTablesDataContext();

  const [selectedTableNames, setSelectedTableNames] = useState<any>([]);
  const [tableQueryDataMap, dispatchQueryAction] = useReducer(
    queryActionReducer,
    {}
  );
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [queriesData, setQueriesData] = useState<any>(null); // NEW STATE

  const { data: getQueries } = useGetQueriesQuery(secretKey, {
    skip: !secretKey || isDataLoaded,
  });

  useEffect(() => {
    if (!isDataLoaded && getQueries?.data?.id) {
      setQueriesData(getQueries.data);
    }
  }, [getQueries, isDataLoaded]);

  const fetchQueries = useCallback(() => {
    if (queriesData?.id && !isEmpty(overallQueryItemMapByTableName)) setIsDataLoaded(true)
    if (queriesData?.queries?.length > 0 && !isEmpty(overallQueryItemMapByTableName)){ 
      dispatchQueryAction({
        action: "TRANSFORM_RESPONSE",
        response: queriesData,
        overallQueryItemMapByTableName,
        setSelectedTableNames,
      });
    };
  }, [queriesData, overallQueryItemMapByTableName]);

  useEffect(() => {
    if (isDataLoaded) return;
    fetchQueries();
  }, [fetchQueries, isDataLoaded, overallQueryItemMapByTableName]);

  const handleOnQueriesRestore = useCallback(() => {
    fetchQueries();
  }, [fetchQueries, queriesData]);

  const handleOnResetAll = useCallback(() => {
    dispatchQueryAction({
      action: 'RESET',
      setSelectedTableNames
    })
  }, [])

  const handleOnTableChange = useCallback(
    (tableName: string, isSelected: boolean) => {
      setSelectedTableNames((current: any[]) => {
        if (isSelected && !current.includes(tableName)) {
          return [...current, tableName];
        } else if (!isSelected) {
          return current.filter((table) => table !== tableName);
        }
        return current;
      });

      dispatchQueryAction({
        action: "ON_TABLE_CHANGE",
        tableName,
        isSelected,
        overallColumns: overallQueryItemMapByTableName[tableName] || [],
      });
    },
    [overallQueryItemMapByTableName]
  );

  const handleOnBodyCheckChange = useCallback(
    (columnName: string, tableName: string, isChecked: boolean) => {
      dispatchQueryAction({
        action: "ON_COLUMN_CHANGE",
        columnName,
        tableName,
        isChecked,
        overAllColumns: overallQueryItemMapByTableName[tableName] || [],
        setSelectedTableNames,
      });
    },
    [overallQueryItemMapByTableName]
  );

  const handleOnSelectAllTables = useCallback(() => {
    dispatchQueryAction({
      action: "SELECT_ALL_TABLES",
      overallQueryItemMapByTableName,
      setSelectedTableNames,
    });
  }, [overallQueryItemMapByTableName]);

  const values: any = useMemo(
    () => ({
      selectedTableNames,
      tableQueryDataMap,
      queriesData,
      handleOnTableChange,
      handleOnBodyCheckChange,
      handleOnQueriesRestore,
      dispatchQueryAction,
      setSelectedTableNames,
      handleOnResetAll,
      handleOnSelectAllTables,
    }),
    [handleOnBodyCheckChange, handleOnQueriesRestore, handleOnResetAll, handleOnSelectAllTables, handleOnTableChange, queriesData, selectedTableNames, tableQueryDataMap]
  );

  return (
    <QueryGeneratorContext.Provider value={values}>
      {children}
    </QueryGeneratorContext.Provider>
  );
};

export default QueryGeneratorProvider;
